import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { PromoFailReason } from "ipctg-inflow-types/InFlowEnums"
import { GatedPromoPaymentPOSTSuccessResponse } from "ipctg-inflow-types/InFlowGatedAPI"

import api from "@/utilities/api"

import Text from "@/globalComponents/atoms/Text"
import TermsAndConditions from "@/globalComponents/molecules/TermsAndConditions"
import Cart from "@/globalComponents/organisms/Cart"
import { cleanEmptyParams } from "@/utilities/etc"
import {
  ExpectedError,
  GatedPromoPaymentError,
} from "@/utilities/expectedErrors"
import {
  gatedCheckoutQueryOptions,
  gatedPaymentIntentQueryOptions,
  gatedRegisterSessionQueryOptions,
} from "@/utilities/hooks/queries/gated"
import { log } from "@/utilities/logging"

export const Route = createFileRoute(
  "/qr/$qrLookupID/gated/$pamID/$sessionID/cart"
)({
  staticData: {
    pageName: "Cart",
  },
  validateSearch: (search: Record<string, unknown>): { from?: string } => {
    return cleanEmptyParams({
      from: (search.from as string) || "",
    })
  },
  beforeLoad: async ({ context, params }) => {
    await context.queryClient.fetchQuery(
      gatedRegisterSessionQueryOptions({
        sessionID: params.sessionID,
        phone: null,
        isInLane: true,
        isReregistering: false,
      })
    )
  },
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { qrLookupID, sessionID, pamID } = Route.useParams()
  const queryClient = useQueryClient()

  const {
    data: checkout,
    refetch,
    isRefetching,
  } = useSuspenseQuery(gatedCheckoutQueryOptions({ sessionID }))

  const applyPromotionMutation = useMutation({
    mutationFn: applyPromotionMutationFn,
    onSuccess: applyPromotionOnSuccess,
    onError: applyPromotionOnError,
  })

  const onCheckoutMutation = useMutation({
    mutationFn: onCheckoutMutationFn,
  })

  return (
    <>
      <Cart
        productType="rate"
        transaction={checkout.transactionDetails}
        mutations={{
          applyPromotion: applyPromotionMutation,
          resetPromotion: {
            mutate: () => refetch(),
            isPending: isRefetching,
          },
          onGoNext: onCheckoutMutation,
        }}
      />
      <Text level={3} className="center">
        <TermsAndConditions qrLookupID={qrLookupID} />
      </Text>
    </>
  )

  async function applyPromotionMutationFn(promoCode: string) {
    if (!promoCode) {
      throw new GatedPromoPaymentError(PromoFailReason.InvalidCode)
    }

    const rsp = await api.gated.promoPayment.POST({ promoCode })

    if ("failReason" in rsp) {
      throw new GatedPromoPaymentError(rsp.failReason)
    }

    return rsp
  }

  function applyPromotionOnSuccess(rsp: GatedPromoPaymentPOSTSuccessResponse) {
    queryClient.setQueryData(
      gatedCheckoutQueryOptions({ sessionID }).queryKey,
      rsp
    )
    resetPaymentIntent()
  }

  /**
   * The payment intent is built from the pre-discount transaction, so it has to
   * be rebuilt on the checkout page now that the amount due has changed.
   */
  function resetPaymentIntent() {
    queryClient.removeQueries({
      ...gatedPaymentIntentQueryOptions({ sessionID, pamID }),
    })
  }

  function applyPromotionOnError(error: Error) {
    log({
      level: "error",
      flow: "gated",
      scope: "fetchResponse.promoPayment",
      message: "Failed to apply promo code",
      meta: { error },
      sentry: error instanceof ExpectedError ? "breadcrumb" : "exception",
      exception: error,
    })
  }

  async function onCheckoutMutationFn(promoCode: string) {
    if (promoCode) {
      try {
        await applyPromotionMutationFn(promoCode)
        resetPaymentIntent()
      } catch (error) {
        log({
          level: "warn",
          flow: "gated",
          scope: "fetchResponse.promoPayment",
          message:
            "Failed to apply promo code at checkout, continuing without it",
          meta: { error },
        })
      }
    }

    await navigate({
      to: "/qr/$qrLookupID/gated/$pamID/$sessionID/checkout",
    })
  }
}
